<template>
  <div
    class="sn-multi-file-input-deprecated"
    :style="boxStyles"
  >
    <template
      v-if="!processing"
    >
      <v-row
        v-for="(fileName) in fileNames"
        :key="fileName.name"
        no-gutters
        align="center"
        class="added-file mb-6"
        :data-cy="fileName.name + '-doc'"
      >
        <v-col cols="auto">
          <sn-avatar
            background-class="sn-background-brand-01"
            icon="$snDocument"
            :icon-style="iconStyle"
          />
        </v-col>
        <v-col
          class="ml-4"
        >
          <sn-text-field
            :value="fileName.isEditing ? fileName.name : fileName.name + '.' + fileName.suffix"
            :class="fileName.isEditing ? '' : 'sn-text-field--inline'"
            :suffix="fileName.isEditing ? '.' + fileName.suffix : ''"
            data-cy="file-name"
            @focus="fileName.isEditing = true"
            @blur="changeFileName(fileName, $event)"
          />
        </v-col>
        <v-col
          v-if="mayRemove"
          cols="auto"
          class="ml-4"
        >
          <v-icon
            color="snPrimary"
            size="24"
            type=""
            @click.stop="removeFile(fileName.localGuid)"
          >
            $snExitCircle
          </v-icon>
        </v-col>
      </v-row>
      <v-divider
        v-if="fileNames.length && fileNames.length < numberOfDocsAllowed"
        class="mb-6 sn-background-gray-04"
      />
      <v-row
        v-if="fileNames.length < numberOfDocsAllowed"
        no-gutters
        align="center"
        justify="center"
        class="drop-zone"
        data-cy="drop-file-area"
        @dragover.prevent
        @drop.prevent
        @drop="handleDrop"
        @click="handleDropZoneClick"
      >
        <div
          :class="$vuetify.breakpoint.smAndDown ? 'w-full' : ''"
        >
          <sn-btn
            text="Upload files"
            type="secondary"
            :size="$vuetify.breakpoint.smAndDown ? 'full-width' : 'standard'"
            data-cy="select-file-btn"
            prepend-icon="sn-icon-upload"
          />
        </div>
        <span class="d-none d-sm-inline ml-4 sn-text-primary-02">
          or drop your files here
        </span>
        <input
          ref="fileInput"
          :accept="accept"
          name="fileInput"
          style="display: none"
          type="file"
          data-cy="file-input"
          multiple
          @change="handleInput"
        >
      </v-row>
      <div
        v-if="declinedUploadType"
        class="d-flex align-center my-4"
      >
        <v-icon
          color="snError"
          size="24"
          type=""
        >
          $snWarning
        </v-icon>
        <div
          class="sn-body-2 sn-text-error ml-2"
          data-cy="error-state-text"
        >
          {{ uploadErrorText }}
        </div>
      </div>
    </template>
    <div
      v-else
      class=""
    >
      <div
        v-for="(fileName, index) in fileNames"
        :key="fileName.name"
      >
        <div class="d-flex justify-space-between mb-4">
          <div class="sn-body-1 sn-text-primary">
            {{ fileName.name + '.' + fileName.suffix }}
          </div>
        </div>
        <div>
          <v-progress-linear
            color="snBrand"
            background-color="snGray02"
            indeterminate
            rounded
            height="8"
          />
        </div>
        <v-divider
          v-if="index < fileNames.length - 1"
          class="my-6"
        />
      </div>
    </div>
  </div>
</template>

<script>

import snBtn from '~/components/snui/v2/snBtn/snBtn'
import snAvatar from '~/components/snui/v2/snAvatar/snAvatar'
import snTextField from '~/components/snui/v2/snTextField/snTextField'
import { snBrand } from '~/assets/css/snui/_snColorVariables.scss'

export default {
  components: {
    snAvatar,
    snBtn,
    snTextField
  },
  props: {
    accept: {
      type: String,
      required: false,
      default: undefined
    },
    processing: {
      type: Boolean,
      default: false,
      required: false
    },
    mayRemove: {
      type: Boolean,
      default: false,
      required: false
    },
    height: {
      type: Number,
      default: 100,
      required: false
    },
    fileNames: {
      type: Array,
      default: () => [],
      required: true
    },
    numberOfDocsAllowed: {
      type: Number,
      default: 100,
      required: false
    }
  },
  data () {
    return {
      declinedUploadType: null
    }
  },
  computed: {
    boxStyles () {
      return { '--box-height': this.height + 'px' }
    },
    iconStyle () {
      return `color: ${snBrand}`
    },
    formattedAcceptList () {
      return this.accept.split(',').map((type) => type.trim()).filter((type) => type.includes('.')).join(', ').replace(/, ((?:.(?!, ))+)$/, ' or $1')
    },
    uploadErrorText () {
      return `${this.declinedUploadType} file types are not allowed, please upload a ${this.formattedAcceptList}`
    }
  },
  methods: {
    handleInput (event) {
      this.declinedUploadType = null
      this.addFiles(event.target.files)
    },
    handleDrop (event) {
      const droppedFiles = event.dataTransfer.files
      this.declinedUploadType = null
      for (let i = 0; i < droppedFiles.length; i++) {
        if (!this.accept || this.accept.includes(droppedFiles[i].type)) {
          this.addFile(droppedFiles[i])
        } else {
          this.declinedUploadType = '.' + droppedFiles[i].name.split('.').pop()
        }
      }
    },
    handleDropZoneClick () {
      this.$refs.fileInput.click()
    },
    addFiles (files) {
      if (files.length) {
        for (let i = 0; i < files.length; i++) {
          this.addFile(files[i])
        }
      }
    },
    addFile (file) {
      if (file) {
        this.$emit('add-file', file)
      }
    },
    removeFile (fileGuid) {
      this.$emit('remove-file', fileGuid)
    },
    changeFileName (fileName, event) {
      this.$emit('file-name-changed', fileName.localGuid, event.target.value)
      fileName.isEditing = false
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~/assets/css/snui';
.w-full {
  width: 100%;
}
.sn-multi-file-input-deprecated {
  display: flex;
  flex-direction: column;

  ::v-deep .sn-text-field--inline {
    &:not(.v-text-field--focused) {
      .v-input__slot {
        border-color: transparent;

        &:hover {
          border-color: $sn-gray-04;
        }

        input {
          text-overflow: ellipsis;
        }
      }
    }
  }

  .row:not(.drop-zone--error) {
    height: 100px;

    &.drop-zone {
      height: auto;

      @media (min-width: $medium-small-screen-size) {
        height: var(--box-height);
        border-radius: 16px;
        border: 2px dashed $sn-gray-04;
        cursor: pointer;
        transition: background-color 200ms;

        &:hover {
          background-color: $sn-brand-01;
          transition: background-color 300ms;
        }
      }
    }

    &.added-file {
      border-radius: 8px;
      border: 1px solid $sn-gray-04;
      padding: 0px 16px;
      background-color: $sn-white;
    }
  }

}
</style>
